/**
 * Shared helpers for decoding the JWT `Role` claim into the app's role model.
 *
 * `JwtSecurityTokenHandler` (backend) collapses repeated claims of the same type into a JSON
 * array under that key once an account holds more than one role, while a single-role account's
 * token keeps the historical bare-string shape. Both shapes must be accepted so existing
 * single-role accounts keep behaving exactly as before.
 */

/** Roles del sistema, alineados con los nombres del backend (RoleConstants.cs). */
export type UserRole = 'Administrator' | 'Developer' | 'Donator';

const ROLE_MAP: Record<string, UserRole> = {
  administrator: 'Administrator',
  developer: 'Developer',
  donator: 'Donator',
};

/**
 * Normalizes the raw JWT `Role` claim value into a deduplicated, lowercased array of strings,
 * regardless of whether it was decoded as a single string, an array of strings, or is absent.
 */
export function normalizeRoleClaim(rawRoleClaim: unknown): string[] {
  const values = Array.isArray(rawRoleClaim)
    ? rawRoleClaim
    : rawRoleClaim != null
      ? [rawRoleClaim]
      : [];

  const seen = new Set<string>();
  for (const value of values) {
    const normalized = String(value).trim().toLowerCase();
    if (normalized) {
      seen.add(normalized);
    }
  }
  return Array.from(seen);
}

/**
 * Maps the raw JWT `Role` claim value to the app's known `UserRole` set, dropping any
 * unrecognized value.
 */
export function mapToUserRoles(rawRoleClaim: unknown): UserRole[] {
  return normalizeRoleClaim(rawRoleClaim)
    .map((role) => ROLE_MAP[role])
    .filter((role): role is UserRole => role !== undefined);
}
